/**
 * The printed eligibility report, as data (rcm-28). Pure functions only.
 *
 * The figures are the calendar modal's figures. The modal reads one engine
 * (readinessMock: each visit's estimate lines, its cap, its opportunities), and
 * this file reads the same visit, so the report cannot drift from the modal.
 * Only the patient's identity and state come from the directory (patientsMock).
 */
import { getPatient, type InsState } from '@/features/patients/directory/patientsMock';
import {
  LOCATIONS,
  MOCK_NOW_MIN,
  MOCK_TODAY_ISO,
  NEXT_WEEK_MONDAY,
  PROVIDERS,
  SETTINGS_HIDDEN_OPPORTUNITIES,
  THIS_WEEK_MONDAY,
  clockTime,
  parseIso,
  parseStamp,
  visibleOpportunities,
  visitById,
  visitsForDate,
  weekdaysOf,
  type Visit,
  cappedTotals,
} from '@/features/calendar/readinessMock';

/** The practice block. The name is the schedule's location; address, phone and NPI are from the rcm-28 mockup. */
export const PRACTICE = {
  name: LOCATIONS[0] ?? 'Alamo Heights Dental',
  address: '5100 Broadway St, San Antonio, TX 78209',
  phone: '(210) 555-0100',
  npi: '1427593068',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five'];
/** Read date for the payer manual (a fixed mock date). */
const MANUAL_READ = 'Aug 10, 2026';

/** Where a patient's coverage stands, as this report prints it. */
export type ReportState = 'verified' | 'pre_auth' | 'no_insurance' | 'coverage_ended' | 'unchecked';

export interface ReportLine {
  key: string;
  cdt: string | null;
  label: string;
  fee: number;
  /** Null unless the state shows amounts (verified). */
  allowed: number | null;
  /** Null unless the state shows amounts (verified). */
  plan: number | null;
  /** Null unless the state shows amounts (verified). */
  patient: number | null;
}

/** The modal's cap facts (readinessMock CapNote), printed in the modal's words. */
export interface CapFacts {
  wouldPay: number;
  left: number;
  uncappedPatient: number;
}

export interface OpportunityRow {
  key: string;
  title: string;
  why: string;
  /** "Plan would pay" for a plan amount, "Worth" for a value that is not a plan payment. */
  label: string;
  amount: string;
}

export interface SourceRow {
  key: string;
  title: string;
  detail: string;
}

export interface CardFact {
  value: string;
  sub: string;
}

export interface ReportModel {
  state: ReportState;
  patientName: string;
  firstName: string;
  dob: string;
  chart: string;
  visit: { dateLong: string; time: string; provider: string } | null;
  plan: { payer: string; payerShort: string; type: string; network: string } | null;
  memberId: string | null;
  checkedLabel: string | null;
  checkFailed: boolean;
  sourceCount: number | null;
  showAmountColumns: boolean;
  lines: ReportLine[];
  /** Sums of the visit's lines before the yearly maximum: the procedure table's totals row. */
  totals: { fee: number; allowed: number; plan: number; patient: number };
  /** What the plan pays and the patient owes once the yearly maximum applies: the hero and the cap note. */
  pays: { plan: number; patient: number };
  writeOff: number;
  cap: CapFacts | null;
  yearly: CardFact | null;
  ortho: CardFact | null;
  deductible: CardFact | null;
  planYear: { range: string; year: string } | null;
  coverageEnded: string | null;
  opportunities: OpportunityRow[];
  hiddenCount: number;
  sources: SourceRow[];
  snapshotId: string;
  preparedLabel: string;
}

/* ---------------------------------------------------------------- money, dates */

/** $1,234.50, always cents. */
export function usd2(n: number): string {
  const [whole = '0', cents = '00'] = Math.abs(n).toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${n < 0 ? '−' : ''}$${grouped}.${cents}`;
}

/** "$50, met Mar 3" → "$50.00, met Mar 3": every whole dollar amount in a mock sentence gets cents. */
function centsIn(text: string): string {
  return text.replace(/\$(\d+)(?![\d.])/g, (_m, d: string) => `$${d}.00`);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function shortDate(d: Date): string {
  return `${MONTHS[d.getMonth()] ?? ''} ${String(d.getDate())}`;
}

/** "Oct 16, 2026" */
function fullDate(d: Date): string {
  return `${shortDate(d)}, ${String(d.getFullYear())}`;
}

/** "Friday, October 16, 2026" */
function longDate(d: Date): string {
  return `${WEEKDAYS_LONG[d.getDay()] ?? ''}, ${MONTHS_LONG[d.getMonth()] ?? ''} ${String(d.getDate())}, ${String(d.getFullYear())}`;
}

function fnv(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/* ------------------------------------------------------------- state mapping */

/**
 * The directory's insurance state decides which page prints. The mapping is
 * deterministic per patient id because the directory row is.
 *   verified        → full estimate
 *   pre_auth        → amounts "Not yet known"
 *   no_insurance    → the practice's fees only
 *   coverage_ended  → "Coverage ended {date}", no estimate
 *   checking / not_checked → "Coverage has not been checked yet", no amounts
 */
export function reportStateOf(s: InsState): ReportState {
  switch (s) {
    case 'verified':
      return 'verified';
    case 'pre_auth':
      return 'pre_auth';
    case 'no_insurance':
      return 'no_insurance';
    case 'coverage_ended':
      return 'coverage_ended';
    case 'checking':
    case 'not_checked':
      return 'unchecked';
  }
}

/* -------------------------------------------------------------- visit pools */

function allVisits(): Visit[] {
  const days = [...weekdaysOf(THIS_WEEK_MONDAY), ...weekdaysOf(NEXT_WEEK_MONDAY)];
  return days.flatMap((iso) => visitsForDate(iso));
}

/** The check's day, if the visit has been checked. Null when it hasn't. */
function checkedDayMs(v: Visit): number | null {
  return v.checkedAt === null ? null : parseStamp(v.checkedAt).setHours(0, 0, 0, 0);
}

let poolCache: Record<ReportState, Visit[]> | null = null;

/**
 * The schedule's visits, grouped by the state each can stand in for. The
 * schedule and the directory share no patient ids, so a patient's visit is
 * picked from their state's pool by a hash of the id. A check dated after
 * today can't be quoted on today's page, so those visits are left out.
 */
function pools(): Record<ReportState, Visit[]> {
  if (poolCache) return poolCache;
  const todayMs = parseIso(MOCK_TODAY_ISO).getTime();
  const usable = allVisits().filter((v) => {
    if (v.dateIso < MOCK_TODAY_ISO) return false;
    const day = checkedDayMs(v);
    return day === null || day <= todayMs;
  });
  poolCache = {
    verified: usable.filter((v) => v.state === 'ready' && v.plan !== null && v.estimate.every((l) => l.mode === 'known')),
    pre_auth: usable.filter((v) => v.state === 'not_ready' && v.line.toLowerCase().includes('pre-auth')),
    no_insurance: usable.filter((v) => v.plan === null),
    coverage_ended: usable.filter((v) => v.coverageEnded !== undefined),
    unchecked: usable.filter((v) => v.state === 'checking'),
  };
  return poolCache;
}

function pickVisit(seed: string, state: ReportState): Visit | null {
  const pool = pools()[state];
  if (pool.length === 0) return null;
  return pool[fnv(seed) % pool.length] ?? null;
}

/* ------------------------------------------------------------------- pieces */

/**
 * "Delta Dental of Texas · PPO" → payer "Delta Dental of Texas", type "PPO".
 * "Cigna PPO" → payer "Cigna", type "PPO". The short payer is the visit's own.
 */
function parsePlan(text: string | null, payerShort: string | null): ReportModel['plan'] {
  if (text === null) return null;
  if (text.includes('·')) {
    const [left = text, right] = text.split('·').map((s) => s.trim());
    const parts = left.split(/\s+/);
    return { payer: left, payerShort: payerShort ?? parts[0] ?? left, type: right ?? 'PPO', network: 'in network' };
  }
  const tokens = text.trim().split(/\s+/);
  const last = tokens[tokens.length - 1] ?? '';
  const payer = tokens.length > 1 ? tokens.slice(0, -1).join(' ') : text.trim();
  return {
    payer,
    payerShort: payerShort ?? (tokens[0] ?? payer),
    type: /HMO/i.test(last) ? 'HMO' : 'PPO',
    network: 'in network',
  };
}

function providerLabel(id: string): string {
  const p = PROVIDERS.find((x) => x.id === id);
  if (!p) return 'your provider';
  return p.kind === 'hygiene' ? `${p.name.replace(/^Hygiene\s*·\s*/, '')}, hygiene` : p.name;
}

function sum(xs: number[]): number {
  return xs.reduce((a, b) => a + b, 0);
}

/** The modal's rule for when the opportunities pane has nothing to offer. */
function opportunityBlocked(visit: Visit): boolean {
  return visit.plan === null || visit.coverageEnded !== undefined || visit.yearlyMaxLeft === null;
}

/** The modal's opportunities, in the modal's order, with the modal's amounts. */
function opportunityRows(visit: Visit): OpportunityRow[] {
  if (opportunityBlocked(visit)) return [];
  return visibleOpportunities(visit).map((o) => ({
    key: o.id,
    title: o.name,
    why: o.line,
    label: o.kind === 'plan' ? 'Plan would pay' : 'Worth',
    amount: usd2(o.amount),
  }));
}

/** The sources that answered the check, in D49 order. Times come from the check stamp; the wording is the report's own. */
function sourceRows(args: {
  checked: Date;
  count: number;
  payer: string;
  payerShort: string;
  codes: string;
}): SourceRow[] {
  const { checked, count, payer, payerShort, codes } = args;
  const day = shortDate(checked);
  const mins = checked.getHours() * 60 + checked.getMinutes();
  const all: SourceRow[] = [
    {
      key: 'history',
      title: `This practice's claim history, ${day} at ${clockTime(5 * 60 + 40)}`,
      detail: `Allowed amounts for ${codes}`,
    },
    {
      key: 'stedi',
      title: `Checked with Stedi, ${day} at ${clockTime(mins)}`,
      detail: 'Plan, member, coverage dates and yearly maximum',
    },
    {
      key: 'portal',
      title: `${payerShort} provider portal, ${day} at ${clockTime(mins + 2)}`,
      detail: 'Remaining maximum confirmed',
    },
    {
      key: 'manual',
      title: `${payer} provider manual, read ${MANUAL_READ}`,
      detail: 'Frequency and age rules for these codes',
    },
    {
      key: 'ai_call',
      title: `AI call to ${payerShort}, ${day} at ${clockTime(mins + 20)}`,
      detail: 'A rule the other sources did not return',
    },
  ];
  // D49 order. The first `count` of them answered.
  return all.slice(0, Math.max(0, count));
}

function joinCodes(lines: ReportLine[]): string {
  const codes = [...new Set(lines.map((l) => l.cdt).filter((c): c is string => c !== null))];
  return codes.length > 0 ? codes.join(', ') : 'these procedures';
}

/* ------------------------------------------------------------------ the model */

/**
 * Build the report for one patient id, or null when the id is unknown.
 * Lines, cap, totals and opportunities are the visit's own (the modal's), so
 * every figure matches the modal to the cent.
 */
export function buildReport(patientId: string | undefined, visitId?: string | null): ReportModel | null {
  // The calendar modal opens the exact visit it shows, so its figures are that visit's, to the cent.
  const scheduled = visitId ? visitById(visitId) : null;
  if (scheduled) {
    return reportFor(
      { key: scheduled.id, name: scheduled.patient, dob: scheduled.dob, chart: scheduled.chart },
      stateOfVisit(scheduled),
      scheduled,
    );
  }
  const row = getPatient(patientId);
  if (!row) return null;
  const state = reportStateOf(row.insState);
  return reportFor(
    { key: row.id, name: row.name, dob: row.dobLabel, chart: row.chart },
    state,
    pickVisit(`${row.id}|${state}`, state),
  );
}

/** The report's state for a scheduled visit: the same reading the modal makes of it. */
function stateOfVisit(v: Visit): ReportState {
  if (v.plan === null) return 'no_insurance';
  if (v.coverageEnded !== undefined) return 'coverage_ended';
  if (v.state === 'ready') return 'verified';
  if (v.state === 'not_ready' && v.line.toLowerCase().includes('pre-auth')) return 'pre_auth';
  // Checking, or a plan that could not be found or confirmed: nothing is verified yet.
  return 'unchecked';
}

function reportFor(
  who: { key: string; name: string; dob: string; chart: string },
  state: ReportState,
  visit: Visit | null,
): ReportModel {
  const today = parseIso(MOCK_TODAY_ISO);
  const year = String(today.getFullYear());
  const checkedAt = visit?.checkedAt ? parseStamp(visit.checkedAt) : null;
  const plan = visit ? parsePlan(visit.plan, visit.payer) : null;
  const amountsKnown = state === 'verified';
  const showAmountColumns = state === 'verified' || state === 'pre_auth';
  const planState = state === 'verified' || state === 'pre_auth';

  const lines: ReportLine[] = (visit?.estimate ?? []).map((l, i) => {
    const known = amountsKnown && l.mode === 'known';
    return {
      key: `${l.cdt ?? l.label}-${String(i)}`,
      cdt: l.cdt ?? null,
      label: l.label,
      fee: l.fee,
      allowed: known ? l.allowed : null,
      plan: known ? l.insurance : null,
      patient: known ? l.patient : null,
    };
  });

  // Totals of the lines before the cap, as the table's totals row shows them.
  const totals = {
    fee: sum(lines.map((l) => l.fee)),
    allowed: sum(lines.map((l) => l.allowed ?? 0)),
    plan: sum(lines.map((l) => l.plan ?? 0)),
    patient: sum(lines.map((l) => l.patient ?? 0)),
  };
  // The amounts after the yearly maximum, from the one shared calculation the modal uses.
  const capped = amountsKnown ? cappedTotals(visit?.estimate ?? [], visit?.cap ?? null) : null;
  const pays = { plan: capped?.plan ?? 0, patient: capped?.patient ?? 0 };

  const sourceCount = visit && checkedAt && (planState || state === 'coverage_ended') ? visit.sources : null;
  const sources: SourceRow[] =
    checkedAt && plan && sourceCount !== null
      ? sourceRows({
          checked: checkedAt,
          count: sourceCount,
          payer: plan.payer,
          payerShort: plan.payerShort,
          codes: joinCodes(lines),
        })
      : [];

  const ymd = `${year}${pad2(today.getMonth() + 1)}${pad2(today.getDate())}`;
  const hm = checkedAt ? checkedAt.getHours() * 60 + checkedAt.getMinutes() : MOCK_NOW_MIN;
  const snapshotId = `snap-${ymd}-${pad2(Math.floor(hm / 60))}${pad2(hm % 60)}-${fnv(`${who.key}|${ymd}`).toString(16).padStart(8, '0').slice(-4)}`;

  const firstName = who.name.split(' ')[0] ?? who.name;
  const ortho = visit?.ortho ?? null;

  return {
    state,
    patientName: who.name,
    firstName,
    dob: who.dob,
    chart: who.chart,
    visit: visit
      ? {
          dateLong: longDate(parseIso(visit.dateIso)),
          time: clockTime(visit.startMin),
          provider: providerLabel(visit.providerId),
        }
      : null,
    plan,
    memberId: visit?.member ?? null,
    checkedLabel: checkedAt
      ? `${shortDate(checkedAt)}, ${clockTime(checkedAt.getHours() * 60 + checkedAt.getMinutes())}`
      : null,
    checkFailed: visit?.checkFailed ?? false,
    sourceCount,
    showAmountColumns,
    lines,
    totals,
    pays,
    writeOff: amountsKnown ? totals.fee - totals.allowed : 0,
    cap: amountsKnown && visit?.cap ? { wouldPay: visit.cap.wouldPay, left: visit.cap.left, uncappedPatient: visit.cap.uncappedPatient } : null,
    yearly:
      planState && visit && visit.yearlyMaxLeft !== null
        ? { value: `${usd2(visit.yearlyMaxLeft)} left`, sub: visit.yearlyMax !== null ? `of ${usd2(visit.yearlyMax)}` : '' }
        : null,
    ortho:
      planState && ortho
        ? { value: `${usd2(ortho.left)} left`, sub: ortho.left === ortho.max ? 'none used' : `of ${usd2(ortho.max)}` }
        : null,
    deductible: planState && visit?.deductible ? { value: visit.deductible.label, sub: centsIn(visit.deductible.sub) } : null,
    planYear: planState && visit?.planYear ? { range: visit.planYear.range, year: visit.planYear.year } : null,
    coverageEnded: visit?.coverageEnded ?? null,
    // Pre-auth: a plan amount for the pending benefit would be a guess, so none is offered (rcm-28 §4).
    opportunities: visit && state === 'verified' ? opportunityRows(visit) : [],
    hiddenCount: SETTINGS_HIDDEN_OPPORTUNITIES.length,
    sources,
    snapshotId,
    preparedLabel: fullDate(today),
  };
}

/** "One", "Two" for the settings sentence. */
export function countWord(n: number): string {
  return WORDS[n] ?? String(n);
}
